import Typesense from 'typesense';
import { appendFilterClause } from '../build-filter-by';
import {
  DEFAULT_SEARCH_ITEMS_PER_PAGE,
  DEFAULT_TYPESENSE_COLLECTION,
  TYPESENSE_SEARCH_STATUS_PUBLISH,
} from '../constants';
import {
  MAIN_SEARCH_ADDITIONAL_PARAMETERS,
  TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
  normalizeTypesenseSort,
  typesenseNavigationTypeInstantSearchFilter,
} from '../typesense-search-config';

export const typesenseServerClient = new Typesense.Client({
  apiKey: process.env.NEXT_PUBLIC_TYPESENSE_SEARCH_ONLY_API_KEY ?? '',
  nodes: [
    {
      host: process.env.NEXT_PUBLIC_TYPESENSE_HOST ?? 'localhost',
      port: Number(process.env.NEXT_PUBLIC_TYPESENSE_PORT) || 8108,
      protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL ?? 'http',
    },
  ],
});

export const getProductBySku = (sku: string) =>
  typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents(sku)
    .retrieve();

/**
 * Fetch full product documents by sku.
 * Uses document retrieval by id (document id = sku) so every sku returns one document and order is preserved.
 */
export const getProductsBySkus = async (skus: string[]): Promise<any[]> => {
  if (!skus.length) return [];
  const results = await Promise.allSettled(
    skus.map((sku) =>
      typesenseServerClient
        .collections(DEFAULT_TYPESENSE_COLLECTION)
        .documents(sku)
        .retrieve(),
    ),
  );
  return results
    .map((r) => (r.status === 'fulfilled' ? r.value : null))
    .filter(Boolean);
};

/**
 * Fetch full product documents by numeric product ids.
 * Uses search with filter_by product_id:[...] (Typesense schema uses product_id).
 * Preserves order of ids in the returned array.
 */
export const getProductsByIds = async (ids: number[]): Promise<any[]> => {
  if (!ids.length) return [];
  const filterBy = `${TYPESENSE_SEARCH_STATUS_PUBLISH} && product_id:[${ids.join(',')}]`;
  const searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by: filterBy,
    per_page: Math.max(ids.length, 250),
  };
  const result = await typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents()
    .search(searchParameters);
  const hits = result.hits?.map((hit: any) => hit.document) ?? [];
  const byProductId = new Map(
    hits.map((doc: any) => [Number(doc.product_id ?? doc.id ?? doc.sku), doc]),
  );
  return ids.map((id) => byProductId.get(id)).filter(Boolean);
};

/** Escape a value for Typesense `field:=`backtick quoting. */
export function escapeTypesenseFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/`/g, '\\`');
}

export async function getPublishedProductsByCategoryName(
  categoryName: string,
  perPage = 24,
): Promise<any[]> {
  const name = categoryName.trim();
  if (!name) return [];
  const escaped = escapeTypesenseFilterValue(name);
  const searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by: `${TYPESENSE_SEARCH_STATUS_PUBLISH} && categories.name:=\`${escaped}\``,
    sort_by: MAIN_SEARCH_ADDITIONAL_PARAMETERS.sort_by,
    per_page: perPage,
  };
  const result = await typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents()
    .search(searchParameters);
  return result.hits?.map((hit: any) => hit.document) ?? [];
}

/** Same as {@link getPublishedProductsByCategoryName} but matches `categories.slug`. */
export async function getPublishedProductsByCategorySlug(
  categorySlug: string,
  perPage = 24,
): Promise<any[]> {
  const slug = categorySlug.trim();
  if (!slug) return [];
  const escaped = escapeTypesenseFilterValue(slug);
  const searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by: `${TYPESENSE_SEARCH_STATUS_PUBLISH} && categories.slug:=\`${escaped}\``,
    sort_by: MAIN_SEARCH_ADDITIONAL_PARAMETERS.sort_by,
    per_page: perPage,
  };
  const result = await typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents()
    .search(searchParameters);
  return result.hits?.map((hit: any) => hit.document) ?? [];
}

/**
 * Slugs of categories that have at least one published product in Typesense.
 * Used by the static sitemap to omit empty `/categorias/{slug}/` URLs.
 * Returns `null` when Typesense is unreachable so callers can skip filtering.
 */
export async function getPublishedCategorySlugsWithProducts(): Promise<
  Set<string> | null
> {
  try {
    const result = await typesenseServerClient
      .collections(DEFAULT_TYPESENSE_COLLECTION)
      .documents()
      .search({
        q: '*',
        query_by: 'name',
        filter_by: TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
        facet_by: 'categories.slug',
        max_facet_values: 1000,
        per_page: 0,
      });
    const counts =
      result.facet_counts?.find((f) => f.field_name === 'categories.slug')
        ?.counts ?? [];
    const slugs = new Set<string>();
    for (const entry of counts) {
      const slug = String(entry?.value ?? '').trim();
      if (slug && Number(entry?.count) > 0) slugs.add(slug);
    }
    return slugs;
  } catch {
    return null;
  }
}

/**
 * First page of the shop-type grid (`HomeSearchProvider` + `ProductGridHome`),
 * using the same filter, sort and page size so the server list matches the live hits.
 */
export async function getPublishedProductsByTypeId(
  typeId: string | number,
  perPage = DEFAULT_SEARCH_ITEMS_PER_PAGE,
  categoryIds?: ReadonlyArray<string | number>,
): Promise<any[]> {
  const searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by: `${typesenseNavigationTypeInstantSearchFilter(typeId, categoryIds)} && ${TYPESENSE_PUBLISHED_PRODUCTS_FILTER}`,
    sort_by: MAIN_SEARCH_ADDITIONAL_PARAMETERS.sort_by,
    per_page: perPage,
  };
  const result = await typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents()
    .search(searchParameters);
  return result.hits?.map((hit: any) => hit.document) ?? [];
}

/**
 * First page of a home `DYNAMIC_PRODUCTS` shelf — same filter/sort/page size as
 * `getDynamicProductSearchClient`, so SSR cards match the live InstantSearch hits.
 */
export async function getPublishedProductsByDynamicFilter({
  filters,
  ordering,
  perPage = DEFAULT_SEARCH_ITEMS_PER_PAGE,
}: {
  filters?: string;
  ordering?: string;
  perPage?: number;
}): Promise<any[]> {
  const filter_by = appendFilterClause(
    TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
    filters,
  );

  const sortBy =
    normalizeTypesenseSort(ordering) ??
    MAIN_SEARCH_ADDITIONAL_PARAMETERS.sort_by;

  const searchParameters = {
    q: '*',
    query_by: 'name',
    filter_by,
    sort_by: sortBy,
    per_page: perPage,
  };
  const result = await typesenseServerClient
    .collections(DEFAULT_TYPESENSE_COLLECTION)
    .documents()
    .search(searchParameters);
  return result.hits?.map((hit: any) => hit.document) ?? [];
}

export const getFreshProductPrices = async (skus: string[]) => {
  try {
    const products = await getProductsBySkus(skus);
    return products.map((product: any) => {
      // Extract promotional pack price from product_prices array (price_channel_id === 14)
      const promotionalPackPrice = product.product_prices?.find(
        (price: any) => price.price_channel_id === 14,
      );

      return {
        id: product.sku,
        price: product.price || 0,
        sale_price: product.sale_price || 0,
        promotional_pack_unit_price:
          promotionalPackPrice?.price ||
          promotionalPackPrice?.sale_price ||
          undefined,
        product_prices: product.product_prices || [],
        quantity: product.quantity || 0, // Stock quantity
        in_stock: product.in_stock || product.quantity || 0,
      };
    });
  } catch (error) {
    console.error('Error fetching fresh product prices:', error);
    return [];
  }
};

export const getAssociatedProducts = async (
  parentSku: string,
  associationType: string = 'generico',
) => {
  try {
    // Match the exact format from the search page payload
    const searchParameters = {
      collection: DEFAULT_TYPESENSE_COLLECTION,
      q: '*',
      query_by: 'name,active_ingredients,brand',
      query_by_weights: '1,1,1',
      prefix: true,
      synonym_prefix: true,
      infix: 'always' as const,
      filter_by: `status_id:=2 && $associated_products(parent_sku:=${parentSku} && association_type:='${associationType}')`,
      facet_by: 'categories.name',
      max_facet_values: 10,
      per_page: 20,
      page: 1,
      sort_by: 'sale_price:asc',
      highlight_full_fields: 'name,active_ingredients,brand',
    };

    const result = await typesenseServerClient
      .collections(DEFAULT_TYPESENSE_COLLECTION)
      .documents()
      .search(searchParameters);

    return {
      products: result.hits?.map((hit: any) => hit.document) ?? [],
      facets: result.facet_counts ?? [],
      found: result.found ?? 0,
      out_of: result.out_of ?? 0,
      page: result.page ?? 1,
      search_time_ms: result.search_time_ms ?? 0,
    };
  } catch (error) {
    console.error('Error fetching associated products:', error);
    return {
      products: [],
      facets: [],
      found: 0,
      out_of: 0,
      page: 1,
      search_time_ms: 0,
    };
  }
};
